import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DashboardLayout } from "../dashboard/DashboardLayout";
import {
  createMassterGiftAction,
  updateMassterGiftAction,
} from "../../redux/actions/GiftAction";
import { fetchKarnatakaCities } from "../../redux/actions/customerActions";
import { fetchCustomerTypes } from "../../redux/actions/customerActions";
import { toast } from "react-toastify";
import { FaArrowLeft, FaPlus, FaTrash } from "react-icons/fa";
import { FiUploadCloud } from "react-icons/fi";
import { FullScreenLoader } from "../Components/Loader";
import axiosInstance from "../../api/axiosInstance";
import CityMultiSelectDropdown from "../Components/CityMultiSelectDropdown";

const CreateGiftMaster = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { userInfo } = useSelector((state) => state.auth);
  const { loading } = useSelector((state) => state.gift || {});
  const {
    customerTypes = [],
    loading: customerTypesLoading = false,
    karnatakaCities = [],
    citiesLoading,
  } = useSelector((state) => state.fetchCustomerList || {});
  // const { customerTypes = [], loading: customerTypesLoading = false } =
  //   useSelector((state) => state.fetchCustomerList || {});

  const fileInputRefs = useRef({});

  const BASE_URL = axiosInstance.defaults.baseURL;
  const orderType = (
    userInfo?.user_order_type ||
    userInfo?.data?.user_order_type ||
    userInfo?.order_type ||
    userInfo?.user_type ||
    "loyality"
  ).toLowerCase();

  const isPromotional = orderType === "promotional";
  const qrLabel = isPromotional ? "Points" : "QR Count";

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (imagePath.startsWith("http")) return imagePath;

    const separator =
      BASE_URL.endsWith("/") || imagePath.startsWith("/") ? "" : "/";
    return `${BASE_URL}${separator}${
      imagePath.startsWith("/") ? imagePath.slice(1) : imagePath
    }`;
  };

  const [isEditMode, setIsEditMode] = useState(false);
  const [schemeId, setSchemeId] = useState(null);
  const [formData, setFormData] = useState({
    offer_scheme_name: "",
    gifts: [],
  });
  // Track which gifts are newly added (don't have gift_id or isNew flag)
  const [newGiftIndices, setNewGiftIndices] = useState(new Set());
  useEffect(() => {
    if (customerTypes.length === 0 && !customerTypesLoading) {
      dispatch(fetchCustomerTypes(userInfo.access_token));
    }
  }, []);

  useEffect(() => {
    dispatch(fetchKarnatakaCities(userInfo.access_token));
  }, []);

  // Load prefilled data if editing
  useEffect(() => {
    if (location.state?.scheme) {
      const scheme = location.state.scheme;
      setIsEditMode(true);
      setSchemeId(scheme.scheme_id || scheme.id);
      console.log("Edit mode - scheme gifts:", scheme.gifts);
      setFormData({
        offer_scheme_name: scheme.scheme_name || "",
        gifts: (scheme.gifts || []).map((gift) => ({
          gift_id: gift.gift_id || null,
          gift_name: gift.gift_name || "",
          gift_image: gift.gift_image || "",
          preview: getImageUrl(gift.gift_image) || null,
          expired_at: toDatetimeLocalValue(gift.expired_at) || "",
          cities: gift.cities || [],
          qr_count: gift.qr_count || 0,
          is_additional_gift: gift.is_additional_gift || false,
          isModified: false, // Track if gift was modified
          is_live: gift.is_live || false, // Track if gift is live
        })),
      });
      setNewGiftIndices(new Set()); // No new gifts initially
    }
  }, [location.state]);

  const handleSchemeNameChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      offer_scheme_name: e.target.value,
    }));
  };

  const handleAddGift = () => {
    const newIndex = formData.gifts.length;
    const newGift = {
      gift_name: "",
      gift_image: "",
      preview: null,
      qr_count: 0,
      is_additional_gift: false,
      cities: [],
      expired_at: "",
      isNew: true, // Mark as newly added
      isModified: false,
    };

    setFormData((prev) => ({
      ...prev,
      gifts: [...prev.gifts, newGift],
    }));

    // Track this as a new gift
    setNewGiftIndices((prev) => new Set([...prev, newIndex]));
  };

  const handleExpiredAtChange = (index, value) => {
    if (value) {
      const selected = new Date(value);
      const now = new Date();
      now.setSeconds(0, 0);
      if (selected < now) {
        toast.error("Expiry date cannot be in the past");
        return;
      }
    }
    const newGifts = [...formData.gifts];
    newGifts[index].expired_at = value; // "2025-12-31T23:59" from the input
    newGifts[index].isModified = true;
    setFormData((prev) => ({ ...prev, gifts: newGifts }));
  };

  // UI value ("YYYY-MM-DDTHH:mm") → API value ("YYYY-MM-DD HH:mm:ss")
  const formatExpiredAt = (value) => {
    if (!value) return "";
    const [datePart, timePart] = value.split("T");
    if (!datePart || !timePart) return "";
    const seconds = timePart.length === 5 ? `${timePart}:00` : timePart;
    return `${datePart} ${seconds}`;
  };

  // API value ("YYYY-MM-DD HH:mm:ss") → UI value ("YYYY-MM-DDTHH:mm")
  const toDatetimeLocalValue = (value) => {
    if (!value) return "";
    const [datePart, timePart] = value.split(" ");
    if (!datePart) return "";
    const hhmm = timePart ? timePart.slice(0, 5) : "00:00";
    return `${datePart}T${hhmm}`;
  };
  const getCurrentDateTimeLocal = () => {
    const now = new Date();
    const offset = now.getTimezoneOffset();
    const localDate = new Date(now.getTime() - offset * 60 * 1000);
    return localDate.toISOString().slice(0, 16);
  };
  const handleRemoveGift = (index) => {
    setFormData((prev) => ({
      ...prev,
      gifts: prev.gifts.filter((_, i) => i !== index),
    }));

    // Update indices in newGiftIndices after removal
    setNewGiftIndices((prev) => {
      const updated = new Set();
      prev.forEach((idx) => {
        if (idx < index) updated.add(idx);
        else if (idx > index) updated.add(idx - 1);
      });
      return updated;
    });
  };

  const handleGiftNameChange = (index, value) => {
    const newGifts = [...formData.gifts];
    newGifts[index].gift_name = value;
    newGifts[index].isModified = true;
    setFormData((prev) => ({
      ...prev,
      gifts: newGifts,
    }));
  };

  const handleQrCountChange = (index, value) => {
    const newGifts = [...formData.gifts];
    // Only allow positive integers
    if (/^\d*$/.test(value) || value === "") {
      const parsed = parseInt(value, 10);
      // Reject 0 — only allow empty (while typing) or positive integers
      if (parsed === 0) return;
      newGifts[index].qr_count = value === "" ? 0 : parseInt(value, 10) || 0;
      newGifts[index].isModified = true;
      setFormData((prev) => ({
        ...prev,
        gifts: newGifts,
      }));
    }
  };

  const handleAdditionalGiftChange = (index, checked) => {
    const newGifts = [...formData.gifts];
    newGifts[index].is_additional_gift = checked;
    newGifts[index].isModified = true;
    setFormData((prev) => ({
      ...prev,
      gifts: newGifts,
    }));
  };

  const handleImageUpload = (index, event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["image/png", "image/jpeg", "image/webp"];
    if (!validTypes.includes(file.type)) {
      toast.error("Please upload PNG, JPEG, or WebP image");
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const base64String = e.target.result;
      const newGifts = [...formData.gifts];
      newGifts[index].gift_image = base64String;
      newGifts[index].preview = base64String;
      newGifts[index].isModified = true;
      setFormData((prev) => ({
        ...prev,
        gifts: newGifts,
      }));
    };
    reader.readAsDataURL(file);
  };

  const convertImageToBase64 = async (imagePath) => {
    try {
      // If already base64, return as is
      if (imagePath.startsWith("data:image")) {
        return imagePath;
      }

      // If it's a URL, fetch and convert to base64
      const fullUrl = getImageUrl(imagePath);
      const response = await fetch(fullUrl);
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error converting image to base64:", error);
      return imagePath;
    }
  };

  // const handleSubmit = async (e) => {

  //   e.preventDefault();

  //   // Validation
  //   if (!formData.offer_scheme_name.trim()) {
  //     toast.error("Please enter offer scheme name");
  //     return;
  //   }

  //   if (formData.gifts.length === 0) {
  //     toast.error("Please add at least one gift");
  //     return;
  //   }

  //   // Validate each gift
  //   for (let gift of formData.gifts) {
  //     if (!gift.gift_name.trim()) {
  //       toast.error("Please enter gift name for all gifts");
  //       return;
  //     }
  //     if (!gift.gift_image) {
  //       toast.error("Please upload image for all gifts");
  //       return;
  //     }
  //     if (!gift.qr_count || gift.qr_count <= 0) {
  //       toast.error("Please enter a valid QR count (greater than 0) for all gifts");
  //       return;
  //     }
  //   }

  //   try {
  //     let result;

  //     if (isEditMode) {
  //       // In edit mode, only send modified gifts and new gifts
  //       const modifiedAndNewGifts = formData.gifts.filter(
  //         (gift, index) => newGiftIndices.has(index) || gift.isModified
  //       );

  //       if (modifiedAndNewGifts.length === 0) {
  //         toast.info("No changes made");
  //         return;
  //       }

  //       const giftsWithBase64 = await Promise.all(
  //         modifiedAndNewGifts.map(async (gift) => {
  //           const base64Image = await convertImageToBase64(gift.gift_image);
  //           const giftData = {
  //             gift_name: gift.gift_name,
  //             gift_image: base64Image,
  //             qr_count: gift.qr_count || 0,
  //             is_additional_gift: gift.is_additional_gift || false,
  //           };
  //           // Include gift_id only if it's an existing gift (not new)
  //           if (gift.gift_id) {
  //             giftData.gift_id = gift.gift_id;
  //           }
  //           return giftData;
  //         })
  //       );

  //       const payload = {
  //         scheme_id: schemeId,
  //         offer_scheme_name: formData.offer_scheme_name,
  //         gifts: giftsWithBase64,
  //       };

  //       console.log("Update Payload:", JSON.stringify(payload, null, 2));

  //       result = await dispatch(
  //         updateMassterGiftAction(payload, userInfo.access_token)
  //       );
  //     } else {
  //       // In create mode, send all gifts
  //       const giftsWithBase64 = await Promise.all(
  //         formData.gifts.map(async (gift) => ({
  //           gift_name: gift.gift_name,
  //           gift_image: await convertImageToBase64(gift.gift_image),
  //           qr_count: gift.qr_count || 0,
  //           is_additional_gift: gift.is_additional_gift || false,
  //         }))
  //       );

  //       const payload = {
  //         offer_scheme_name: formData.offer_scheme_name,
  //         gifts: giftsWithBase64,
  //       };

  //       console.log("Create Payload:", JSON.stringify(payload, null, 2));

  //       result = await dispatch(
  //         createMassterGiftAction(payload, userInfo.access_token)
  //       );
  //     }

  //     if (
  //       result.type === "CREATE_GIFT_MASTER_CREATE_SUCCESS" ||
  //       result.type === "UPDATE_GIFT_MASTER_SUCCESS"
  //     ) {
  //       toast.success(
  //         isEditMode
  //           ? "Gift master updated successfully!"
  //           : "Gift master created successfully!"
  //       );
  //       navigate("/dashboard/giftList");
  //     } else {
  //       toast.error(result.payload?.message || "Failed to save gift master");
  //     }
  //   } catch (error) {
  //     console.error("Submit error:", error);
  //     toast.error("Failed to save gift master. Please try again.");
  //   }
  // };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.offer_scheme_name.trim()) {
      toast.error("Please enter offer scheme name");
      return;
    }

    if (formData.gifts.length === 0) {
      toast.error("Please add at least one gift");
      return;
    }

    // Validate each gift
    for (let gift of formData.gifts) {
      if (!gift.gift_name.trim()) {
        toast.error("Please enter gift name for all gifts");
        return;
      }
      if (!gift.gift_image) {
        toast.error("Please upload image for all gifts");
        return;
      }
      if (
        !gift.qr_count ||
        Number(gift.qr_count) <= 0 ||
        gift.qr_count === ""
      ) {
        toast.error(
          `Please enter a valid ${qrLabel} (greater than 0) for all gifts`,
        );
        return;
      }
    }

    try {
      let result;

      if (isEditMode) {
        // In edit mode, send all gifts with the scheme data
        const giftsPayload = await Promise.all(
          formData.gifts.map(async (gift) => {
            const giftData = {
              gift_name: gift.gift_name,
              qr_count: gift.qr_count || 0,
              is_additional_gift: gift.is_additional_gift || false,
            };
            if (gift.cities && gift.cities.length > 0) {
              giftData.cities = gift.cities;
            }
            const isNewGift = newGiftIndices.has(formData.gifts.indexOf(gift));
            if (gift.expired_at) {
              giftData.expired_at = formatExpiredAt(gift.expired_at);
            } else if (isNewGift || gift.isModified) {
              giftData.expired_at = null;
            }
            // Include gift_id if it exists (existing gift)
            if (gift.gift_id) {
              giftData.gift_id = gift.gift_id;
            }

            // Include is_live for existing gifts
            if (gift.is_live !== undefined) {
              giftData.is_live = gift.is_live;
            }

            // Only include gift_image if it was modified or it's a new gift

            if (isNewGift || gift.isModified) {
              // If image was changed or it's a new gift, send base64
              if (gift.gift_image.startsWith("data:image")) {
                giftData.gift_image = gift.gift_image;
              } else {
                // Convert to base64 if needed
                giftData.gift_image = await convertImageToBase64(
                  gift.gift_image,
                );
              }
            }

            return giftData;
          }),
        );

        const payload = {
          offer_scheme_id: schemeId, // ✅ This is the fix - include offer_scheme_id
          offer_scheme_name: formData.offer_scheme_name,
          gifts: giftsPayload,
        };

        console.log("Update Payload:", JSON.stringify(payload, null, 2));

        result = await dispatch(
          updateMassterGiftAction(payload, userInfo.access_token),
        );
      } else {
        // In create mode, send all gifts with base64 images
        const giftsWithBase64 = await Promise.all(
          // formData.gifts.map(async (gift) => ({
          //   gift_name: gift.gift_name,
          //   gift_image: await convertImageToBase64(gift.gift_image),
          //   qr_count: gift.qr_count || 0,
          //   is_additional_gift: gift.is_additional_gift || false,
          // })),

          formData.gifts.map(async (gift) => {
            const giftData = {
              gift_name: gift.gift_name,
              gift_image: await convertImageToBase64(gift.gift_image),
              qr_count: gift.qr_count || 0,
              is_additional_gift: gift.is_additional_gift || false,
            };
            if (gift.cities && gift.cities.length > 0) {
              giftData.cities = gift.cities;
            }
            if (gift.expired_at) {
              giftData.expired_at = formatExpiredAt(gift.expired_at);
            }
            return giftData;
          }),
        );

        const payload = {
          offer_scheme_name: formData.offer_scheme_name,
          gifts: giftsWithBase64,
        };

        console.log("Create Payload:", JSON.stringify(payload, null, 2));

        result = await dispatch(
          createMassterGiftAction(payload, userInfo.access_token),
        );
      }

      if (
        result.type === "CREATE_GIFT_MASTER_CREATE_SUCCESS" ||
        result.type === "UPDATE_GIFT_MASTER_SUCCESS"
      ) {
        toast.success(
          isEditMode
            ? "Gift master updated successfully!"
            : "Gift master created successfully!",
        );
        navigate("/dashboard/giftList");
      } else {
        toast.error(result.payload?.message || "Failed to save gift master");
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error("Failed to save gift master. Please try again.");
    }
  };
  return (
    <DashboardLayout>
      <FullScreenLoader
        show={loading}
        message={
          loading
            ? isEditMode
              ? "Updating gift master..."
              : "Creating gift master..."
            : ""
        }
        variant="primary"
        blurIntensity="md"
      />
      <div className="mt-2 max-w-full mx-auto rounded-lg p-2">
        <div className="max-w-4xl mx-auto mt-4 p-6 bg-white rounded-lg shadow-md">
          <div className="flex items-center space-x-3 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="hover:text-gray-800"
            >
              <FaArrowLeft size={20} />
            </button>
            <h2 className="text-2xl font-bold text-amber-600">
              {isEditMode ? "Edit Gift Master" : "Create Gift Master"}
            </h2>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              {/* Offer Scheme Name */}
              <div className="relative">
                <input
                  type="text"
                  id="offer_scheme_name"
                  name="offer_scheme_name"
                  value={formData.offer_scheme_name}
                  onChange={handleSchemeNameChange}
                  className="peer w-full border border-gray-300 rounded-md px-3 pt-5 pb-2 text-sm focus:outline-none focus:border-amber-500"
                  placeholder=" "
                  required
                />
                <label
                  htmlFor="offer_scheme_name"
                  className="pointer-events-none absolute left-2.5 -top-2 text-xs bg-white px-1 transition-all duration-200 text-gray-500
                  peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400
                  peer-focus:-top-2 peer-focus:text-xs peer-focus:text-amber-500"
                >
                  Offer Scheme Name <span className="text-red-500">*</span>
                </label>
              </div>

              {/* Gifts Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Gifts <span className="text-red-500">*</span>
                  </label>
                  {!isEditMode && (
                    <button
                      type="button"
                      onClick={handleAddGift}
                      className="flex items-center gap-2 px-3 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition duration-300 text-sm font-medium"
                    >
                      <FaPlus size={14} />
                      Add Gift
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  {formData.gifts.map((gift, index) => (
                    <div
                      key={index}
                      className={`border rounded-lg p-4 ${
                        newGiftIndices.has(index) || gift.isModified
                          ? "border-amber-300 bg-amber-50"
                          : "border-gray-300 bg-gray-50"
                      }`}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-700">
                            Gift {index + 1}
                          </h4>
                          {isEditMode && gift.gift_id && (
                            <p className="text-xs text-gray-500">
                              {newGiftIndices.has(index) || gift.isModified
                                ? "Modified"
                                : "Existing"}
                            </p>
                          )}
                          {isEditMode && !gift.is_live && (
                            <p className="text-xs text-red-500 font-semibold">
                              Inactive - Cannot Edit
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveGift(index)}
                          className="text-red-500 hover:text-red-700 transition"
                        >
                          <FaTrash size={16} />
                        </button>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {/* Gift Name */}
                        <div className="relative">
                          <input
                            type="text"
                            value={gift.gift_name}
                            onChange={(e) =>
                              handleGiftNameChange(index, e.target.value)
                            }
                            disabled={isEditMode && !gift.is_live}
                            className={`peer w-full border border-gray-300 rounded-md px-3 pt-5 pb-2 text-sm focus:outline-none focus:border-amber-500 ${
                              isEditMode && !gift.is_live
                                ? "bg-gray-100 cursor-not-allowed opacity-60"
                                : ""
                            }`}
                            placeholder=" "
                            required
                          />
                          <label
                            className="pointer-events-none absolute left-2.5 -top-2 text-xs bg-white px-1 transition-all duration-200 text-gray-500
                            peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400
                            peer-focus:-top-2 peer-focus:text-xs peer-focus:text-amber-500"
                          >
                            Gift Name <span className="text-red-500">*</span>
                          </label>
                        </div>

                        {/* Image Upload */}
                        <div>
                          <input
                            type="file"
                            ref={(el) => (fileInputRefs.current[index] = el)}
                            onChange={(e) => handleImageUpload(index, e)}
                            accept="image/png,image/jpeg,image/webp"
                            className="hidden"
                            disabled={isEditMode && !gift.is_live}
                          />
                          <button
                            type="button"
                            onClick={() => fileInputRefs.current[index].click()}
                            disabled={isEditMode && !gift.is_live}
                            className={`w-full border-2 border-dashed border-amber-300 rounded-lg px-3 py-3 hover:border-amber-500 hover:bg-amber-50 transition duration-300 flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-amber-600 ${
                              isEditMode && !gift.is_live
                                ? "bg-gray-100 cursor-not-allowed opacity-60 border-gray-300 hover:border-gray-300 hover:bg-gray-100 hover:text-gray-600"
                                : ""
                            }`}
                          >
                            <FiUploadCloud size={18} />
                            {gift.gift_image ? "Change" : "Upload"}
                          </button>
                          {gift.preview && (
                            <div className="mt-2 flex justify-center">
                              <img
                                src={gift.preview}
                                alt={`Preview ${index}`}
                                className="h-20 w-20 object-cover rounded-md border border-gray-300"
                                onError={(e) => {
                                  e.target.src =
                                    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='48' height='48'%3E%3Crect width='48' height='48' fill='%23ddd'/%3E%3Ctext x='50%' y='50%' text-anchor='middle' dy='.3em' fill='%23999' font-size='10'%3ENo Image%3C/text%3E%3C/svg%3E";
                                }}
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      {/* QR Count and Additional Gift */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                        {/* QR Count */}
                        <div className="relative">
                          <input
                            type="number"
                            value={gift.qr_count || ""}
                            onChange={(e) =>
                              handleQrCountChange(index, e.target.value)
                            }
                            onWheel={(e) => e.target.blur()}
                            disabled={isEditMode && !gift.is_live}
                            min="1"
                            className={`peer w-full border border-gray-300 rounded-md px-3 pt-5 pb-2 text-sm focus:outline-none focus:border-amber-500 ${
                              isEditMode && !gift.is_live
                                ? "bg-gray-100 cursor-not-allowed opacity-60"
                                : ""
                            }`}
                            placeholder=" "
                            required
                          />
                          <label
                            className="pointer-events-none absolute left-2.5 -top-2 text-xs bg-white px-1 transition-all duration-200 text-gray-500
                            peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400
                            peer-focus:-top-2 peer-focus:text-xs peer-focus:text-amber-500"
                          >
                            {/* QR Count <span className="text-red-500">*</span> */}
                            {qrLabel} <span className="text-red-500">*</span>
                          </label>
                        </div>

                        {/* <div className="relative">
                          <button
                            type="button"
                            onClick={() =>
                              setOpenCityDropdown(
                                openCityDropdown === index ? null : index,
                              )
                            }
                            disabled={isEditMode && !gift.is_live}
                            className={`w-full border border-gray-300 rounded-md px-3 py-2.5 text-left focus:outline-none focus:border-amber-500 transition bg-white flex justify-between items-center text-sm ${
                              isEditMode && !gift.is_live
                                ? "bg-gray-100 cursor-not-allowed opacity-60"
                                : ""
                            }`}
                          >
                            <span
                              className={
                                gift.cities && gift.cities.length
                                  ? "text-gray-900"
                                  : "text-gray-400"
                              }
                            >
                              {citiesLoading
                                ? "Loading cities..."
                                : gift.cities && gift.cities.length > 0
                                  ? gift.cities.join(", ")
                                  : "Select Cities (optional)"}
                            </span>
                            <span className="text-gray-400 text-xs">▼</span>
                          </button>

                          {openCityDropdown === index && (
                            <div className="absolute z-10 mt-1 w-full bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                              {karnatakaCities.map((city) => (
                                <label
                                  key={city}
                                  className="flex items-center gap-2 px-4 py-2 hover:bg-amber-50 cursor-pointer text-sm"
                                >
                                  <input
                                    type="checkbox"
                                    checked={(gift.cities || []).includes(city)}
                                    onChange={() =>
                                      toggleCityForGift(index, city)
                                    }
                                    className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500 cursor-pointer"
                                  />
                                  {city}
                                </label>
                              ))}
                              <div className="border-t border-gray-100 p-2">
                                <button
                                  type="button"
                                  onClick={() => setOpenCityDropdown(null)}
                                  className="w-full text-center text-amber-600 hover:text-amber-700 text-sm font-medium py-1"
                                >
                                  Done
                                </button>
                              </div>
                            </div>
                          )}
                        </div> */}
                        <CityMultiSelectDropdown
                          cities={karnatakaCities}
                          selected={gift.cities || []}
                          loading={citiesLoading}
                          disabled={isEditMode && !gift.is_live}
                          onChange={(newCities) => {
                            const newGifts = [...formData.gifts];
                            newGifts[index].cities = newCities;
                            newGifts[index].isModified = true;
                            setFormData((prev) => ({
                              ...prev,
                              gifts: newGifts,
                            }));
                          }}
                        />

                        {/* Expires At (optional) - placed right after Select Cities */}
                        <div className="relative">
                          <input
                            type="datetime-local"
                            value={gift.expired_at || ""}
                            onChange={(e) =>
                              handleExpiredAtChange(index, e.target.value)
                            }
                            min={getCurrentDateTimeLocal()}
                            disabled={isEditMode && !gift.is_live}
                            className={`peer w-full border border-gray-300 rounded-md px-3 pt-5 pb-2 text-sm focus:outline-none focus:border-amber-500 ${
                              isEditMode && !gift.is_live
                                ? "bg-gray-100 cursor-not-allowed opacity-60"
                                : ""
                            }`}
                            placeholder=" "
                          />
                          <label className="pointer-events-none absolute left-2.5 -top-2 text-xs bg-white px-1 transition-all duration-200 text-gray-500">
                            Expires At
                            <span className="text-xs text-gray-400 font-normal ml-1">
                              (optional)
                            </span>
                          </label>
                        </div>

                        {/* Additional Gift Checkbox */}
                        <div className="flex items-center gap-3 bg-white p-4 rounded-lg border border-gray-200">
                          <input
                            type="checkbox"
                            id={`is_additional_gift_${index}`}
                            checked={gift.is_additional_gift || false}
                            onChange={(e) =>
                              handleAdditionalGiftChange(
                                index,
                                e.target.checked,
                              )
                            }
                            disabled={isEditMode && !gift.is_live}
                            className={`w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-500 cursor-pointer ${
                              isEditMode && !gift.is_live
                                ? "cursor-not-allowed opacity-60"
                                : ""
                            }`}
                          />
                          <label
                            htmlFor={`is_additional_gift_${index}`}
                            className={`text-sm font-medium text-gray-700 cursor-pointer flex-1 ${
                              isEditMode && !gift.is_live
                                ? "cursor-not-allowed opacity-60"
                                : ""
                            }`}
                          >
                            Additional Gift (Bumper Prize)
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {formData.gifts.length === 0 && (
                  <div className="text-center py-6 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                    <p className="text-gray-500 text-sm">
                      No gifts added yet. Click "Add Gift" to get started.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Submit and Cancel Buttons */}
            <div className="flex gap-3 mt-6">
              <button
                type="submit"
                disabled={loading}
                className={`w-auto bg-amber-500 text-white py-2 px-6 rounded-lg hover:bg-amber-600 transition duration-300 font-medium ${
                  loading ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {loading
                  ? isEditMode
                    ? "Updating..."
                    : "Creating..."
                  : isEditMode
                    ? "Update Gift Master"
                    : "Create Gift Master"}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-lg transition duration-300 font-medium"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CreateGiftMaster;
